/**
 * AccessScannerPage.jsx — mobile-first QR entrance scanner
 * ---------------------------------------------------------
 * Mounted at `/dashboard/access/scanner`.  Camera-based QR decoder
 * (html5-qrcode) + manual code entry fallback.  Roles permitted by
 * backend: super_admin / org_admin / event_manager / volunteer.
 *
 * Workflow:
 *   1. Pick an event + (optional) zone + (optional) gate name.
 *   2. Start the scanner.
 *   3. Every detected QR token is POSTed to `/api/events/access/scan`.
 *   4. Result is rendered immediately as "Approved" or one of the
 *      denial states from `aps.SCAN_LABELS`.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import CockpitHero from "../../components/CockpitHero";
import { Html5Qrcode } from "html5-qrcode";
import {
  scanToken, adminListZones, formatApiError,
} from "../../lib/accessPassApi";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Camera, CameraOff, KeyRound, CheckCircle2, XCircle, AlertTriangle,
  Loader2, ScanLine, MapPin, ShieldCheck, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const READER_ID = "diplomexa-qr-reader";

const RESULT_TONE = {
  approved:          { cls: "bg-emerald-500", Icon: CheckCircle2, label: "Access Approved" },
  already_checked_in:{ cls: "bg-amber-500",   Icon: AlertTriangle, label: "Already Checked In" },
  invalid:           { cls: "bg-rose-600",    Icon: XCircle,      label: "Invalid Pass" },
  expired:           { cls: "bg-rose-600",    Icon: XCircle,      label: "Expired Pass" },
  wrong_event:       { cls: "bg-rose-600",    Icon: XCircle,      label: "Wrong Event" },
  wrong_zone:        { cls: "bg-rose-600",    Icon: XCircle,      label: "Wrong Zone" },
  payment_pending:   { cls: "bg-amber-500",   Icon: AlertTriangle, label: "Payment Pending" },
  approval_pending:  { cls: "bg-amber-500",   Icon: AlertTriangle, label: "Approval Pending" },
  revoked:           { cls: "bg-rose-700",    Icon: XCircle,      label: "Pass Revoked" },
  denied:            { cls: "bg-rose-700",    Icon: XCircle,      label: "Access Denied" },
  wrong_day:         { cls: "bg-rose-500",    Icon: XCircle,      label: "Not Valid Today" },
};

export default function AccessScannerPage() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [zones, setZones] = useState([]);
  const [zoneCode, setZoneCode] = useState("");
  const [gateName, setGateName] = useState("");
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [manualToken, setManualToken] = useState("");
  const [processing, setProcessing] = useState(false);
  const qrRef = useRef(null);
  const lastTokenRef = useRef("");
  const lastAtRef = useRef(0);

  // ── Load admin-visible events
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/events");
        const list = r.data?.data || r.data?.rows || r.data || [];
        setEvents(list);
        if (list.length && !eventId) setEventId(list[0].id);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load zones whenever event changes
  useEffect(() => {
    if (!eventId) return;
    (async () => {
      try {
        const r = await adminListZones(eventId);
        setZones(r.rows || []);
        setZoneCode("");
      } catch {
        setZones([]);
      }
    })();
  }, [eventId]);

  const sendToken = useCallback(
    async (token) => {
      if (!eventId) {
        toast.error("Select an event first.");
        return;
      }
      // Debounce duplicate decodes within 3s.
      const now = Date.now();
      if (token === lastTokenRef.current && now - lastAtRef.current < 3000) {
        return;
      }
      lastTokenRef.current = token;
      lastAtRef.current = now;
      setProcessing(true);
      try {
        const r = await scanToken({
          token,
          event_id: eventId,
          zone_code: zoneCode || undefined,
          gate_name: gateName || undefined,
          action: "check_in",
        });
        setLastResult({ ...r, raw_token: token });
      } catch (err) {
        const data = err?.response?.data?.detail || {};
        setLastResult({
          scan_result: data.scan_result || "invalid",
          label: data.label || "Invalid Pass",
          approved: false,
          raw_token: token,
        });
      } finally {
        setProcessing(false);
      }
    },
    [eventId, zoneCode, gateName]
  );

  const startScanner = async () => {
    if (!eventId) {
      toast.error("Pick an event before scanning.");
      return;
    }
    try {
      if (!qrRef.current) qrRef.current = new Html5Qrcode(READER_ID);
      await qrRef.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decoded) => sendToken(decoded),
        () => {}    // ignore individual decode failures
      );
      setScanning(true);
    } catch (e) {
      toast.error(`Camera unavailable: ${e?.message || e}`);
    }
  };

  const stopScanner = async () => {
    if (qrRef.current) {
      try {
        await qrRef.current.stop();
        await qrRef.current.clear();
      } catch {/* noop */}
    }
    setScanning(false);
  };

  useEffect(() => () => { stopScanner(); }, []);

  return (
    <DashboardLayout>
      <div className="px-4 sm:px-8 py-6 max-w-5xl mx-auto">
        <CockpitHero
          testId="scanner-hero"
          tone="tight"
          eyebrow="Volunteer · Access Control"
          eyebrowIcon={ShieldCheck}
          title="Scan"
          titleAccent="AccessPass"
          titleTail="."
          description="Point the camera at the visitor's QR pass — entry decisions run live against the Diplomexa backend with cryptographic verification."
          className="mb-6"
        />

        {/* Context selectors */}
        <div className="rounded-sm border border-slate-200 bg-white p-4 grid sm:grid-cols-3 gap-3">
          <label className="text-sm">
            <span className="text-xs font-semibold text-slate-600 mb-1 block">Event *</span>
            <select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              className={selectCls}
              data-testid="scanner-event-select"
            >
              <option value="">— pick an event —</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="text-xs font-semibold text-slate-600 mb-1 block">Zone</span>
            <select
              value={zoneCode}
              onChange={(e) => setZoneCode(e.target.value)}
              className={selectCls}
              data-testid="scanner-zone-select"
            >
              <option value="">All zones (main entry)</option>
              {zones.map((z) => (
                <option key={z.id} value={z.code}>
                  {z.name} ({z.code})
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="text-xs font-semibold text-slate-600 mb-1 block">Gate / desk name</span>
            <input
              value={gateName}
              onChange={(e) => setGateName(e.target.value)}
              placeholder="e.g. Gate A"
              className={selectCls}
              data-testid="scanner-gate-input"
            />
          </label>
        </div>

        {/* Camera + scan controls */}
        <div className="mt-5 grid lg:grid-cols-[1fr_320px] gap-5">
          <div className="rounded-2xl border bg-[#0A192F] text-white p-4">
            <div
              id={READER_ID}
              className="rounded-xl overflow-hidden bg-black aspect-square sm:aspect-video"
              data-testid="scanner-viewport"
            />
            <div className="mt-3 flex items-center justify-between gap-2">
              {scanning ? (
                <Button
                  onClick={stopScanner}
                  variant="outline"
                  className="bg-white text-[#0A192F]"
                  data-testid="scanner-stop-btn"
                >
                  <CameraOff className="h-4 w-4 mr-2" />
                  Stop camera
                </Button>
              ) : (
                <Button
                  onClick={startScanner}
                  className="bg-[#D4AF37] hover:bg-[#B89227] text-[#0A192F]"
                  data-testid="scanner-start-btn"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Start camera scan
                </Button>
              )}
              {processing && (
                <span className="inline-flex items-center text-xs text-amber-200">
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  Verifying…
                </span>
              )}
            </div>
          </div>

          {/* Manual entry */}
          <div className="rounded-2xl border bg-white p-4">
            <p className="text-sm font-semibold text-[#0A192F] flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-[#D4AF37]" />
              Manual code entry
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Paste a token from a printed pass if the camera fails.
            </p>
            <textarea
              value={manualToken}
              onChange={(e) => setManualToken(e.target.value)}
              rows={4}
              placeholder="payload.signature"
              className={`${selectCls} mt-3 font-mono text-xs`}
              data-testid="scanner-manual-input"
            />
            <Button
              onClick={() => manualToken && sendToken(manualToken.trim())}
              className="mt-3 w-full bg-[#0A192F] hover:bg-[#0F2C4F] text-white"
              disabled={!manualToken || processing}
              data-testid="scanner-manual-submit"
            >
              <ScanLine className="h-4 w-4 mr-2" />
              Submit code
            </Button>
          </div>
        </div>

        {/* Result panel */}
        {lastResult && (
          <ResultPanel result={lastResult} />
        )}
      </div>
    </DashboardLayout>
  );
}

function ResultPanel({ result }) {
  const tone = RESULT_TONE[result.scan_result] || RESULT_TONE.invalid;
  const { Icon } = tone;
  return (
    <div
      className={`mt-5 rounded-2xl text-white px-5 py-5 sm:py-7 ${tone.cls}`}
      data-testid={`scan-result-${result.scan_result}`}
    >
      <div className="flex items-start gap-4">
        <Icon className="h-9 w-9 mt-1" />
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.28em] font-bold opacity-80">
            Scan Result
          </p>
          <h3
            className="text-2xl sm:text-3xl font-bold mt-1"
            data-testid="scan-result-label"
          >
            {result.label || tone.label}
          </h3>
          {result.pass && (
            <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
              <div>
                <p className="opacity-70 text-[10px] uppercase tracking-wider">Pass holder</p>
                <p className="font-bold" data-testid="scan-result-name">
                  {result.pass.full_name || "Unknown"}
                </p>
                <p className="opacity-80 text-xs">{result.pass.email}</p>
              </div>
              <div>
                <p className="opacity-70 text-[10px] uppercase tracking-wider">Pass type</p>
                <p className="font-bold uppercase tracking-wider text-sm">
                  {result.pass.role || result.pass.pass_type || "—"}
                </p>
                <p className="opacity-80 text-xs">{result.pass.organisation || "—"}</p>
              </div>
              {result.zone && (
                <div>
                  <p className="opacity-70 text-[10px] uppercase tracking-wider">Zone</p>
                  <p className="font-bold inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />
                    {result.zone.name} ({result.zone.code})
                  </p>
                </div>
              )}
              {result.pass.event_title && (
                <div>
                  <p className="opacity-70 text-[10px] uppercase tracking-wider">Event</p>
                  <p className="font-bold">{result.pass.event_title}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const selectCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-[#0A192F] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40 focus:border-[#D4AF37]/60";
